import type { FC } from 'react';
import { memo } from 'react';
import { styled, useTheme } from 'styled-components';

import { Button } from '../../Controls/Button/index.tsx';
import Icons from '../../Elements/Icons/index.ts';
import Typography from '../../Typography/index.ts';
import { Notification } from '../Notifications/index.tsx';
import type { NotificationProps } from '../Notifications/types.ts';
import { Modal } from './index.tsx';
import type { ModalProps } from './types.ts';

const StyledModal = styled(Modal)`
  min-height: 150px;
`;

const ContentWrapper = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  & h5 {
    margin: 0;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

interface ModalDialogProps extends Omit<ModalProps, 'tooltip' | 'title' | 'width' | 'height' | 'actions' | 'scroll'> {
  title: string;
  text?: string;
  cancelButtonText: string;
  actionButtonText: string;
  isPendingAction?: boolean;
  error?: boolean;
  onActionButtonClick: () => void;
  dataTestId?: string;
  notification?: {
    title?: NotificationProps['title'];
    body: NotificationProps['body'];
    type?: NotificationProps['type'];
  };
}

export const ModalDialog: FC<ModalDialogProps> = memo(function ModalDialog({
  title,
  text,
  cancelButtonText,
  actionButtonText,
  isPendingAction,
  error = false,
  onActionButtonClick,
  dataTestId,
  notification,
  ...props
}) {
  const { colors } = useTheme();

  return (
    <StyledModal width='400px' height='auto' {...props}>
      <ContentWrapper data-testid='modal-dialog'>
        <TitleWrapper>
          {error && <Icons.Warning size={20} color={colors.critical100} filled />}
          <Typography.SubTitle>{title}</Typography.SubTitle>
        </TitleWrapper>
        <Typography.TextMedium>{text}</Typography.TextMedium>
        {notification && (
          <Notification uncloseable title={notification.title} body={notification.body} type={notification.type ?? 'info'} />
        )}
      </ContentWrapper>
      <Modal.Footer data-testid={dataTestId}>
        <Button ghost onClick={props.state.close} disabled={isPendingAction} dataTestId='cancel-button'>
          {cancelButtonText}
        </Button>
        <Button
          primary={!error}
          critical={error}
          onClick={onActionButtonClick}
          isLoading={isPendingAction}
          disabled={isPendingAction}
          dataTestId='confirm-button'>
          {actionButtonText}
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
});
